/**
 * Admin quote detail page (P16-T10 step 3). Three-panel layout per
 * spec §9.2:
 *
 *   Left   — header + meta (number, status, requester, company,
 *            location, requested_at, age) + "Effective terms preview"
 *   Center — QuoteItemsEditor (active when `requested` or `revised`)
 *   Right  — valid_until picker, payment_terms_override select,
 *            admin_note textarea, audit trail, Save revision +
 *            Cancel quote actions
 *
 * Terminal states (accepted / rejected / cancelled / expired) collapse
 * the right-panel form to a read-only banner. The items table renders
 * in disabled mode in that case.
 *
 * "Effective terms preview" recomputes live as `payment_terms_override`
 * changes — mirrors `AdminQuotesService.findDetail`'s collapse logic
 * (`override ?? company.paymentTerms`). The company default is read
 * from `quote.effective_payment_terms` when override is null, since
 * the service already collapsed both fields.
 *
 * Save revision → POST /v1/admin/quotes/:id/revise. Cancel quote →
 * POST /v1/admin/quotes/:id/cancel (confirm modal).
 *
 * Plan T10 step 6: 2 happy-path tests (load + submit revision).
 * Validation lives in `quote-validation.ts` and is exhaustively
 * unit-tested separately.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AuditLogTimeline,
  Button,
  Card,
  Hairline,
  Modal,
  Numeric,
  Select,
  StatusPill,
  Textarea,
  type Tone,
} from "@wmart/ui";
import { api, ApiError } from "../../lib/api-client.js";
import { formatGBP, formatTimestamp } from "../../lib/format.js";
import { useToast } from "../../lib/toast.js";
import {
  parseRevisionForm,
  type ReviseFormState,
} from "../../lib/quote-validation.js";
import {
  QuoteItemsEditor,
  type QuoteEditorRow,
} from "./QuoteItemsEditor.js";
import type {
  EffectivePaymentTerms,
  QuoteDetail,
  QuoteStatus,
} from "@wmart/shared-types";

/**
 * Non-nullable variant of `QuotePaymentTermsOverride` used as the form
 * state value. The wire-shape type is nullable (null = "inherit") but
 * the form state encodes "inherit" as the empty string so the
 * `<select>` `value` prop accepts a non-null primitive. The parser
 * (`parseRevisionForm`) collapses `""` → `null` at submit time.
 *
 * P17 adds `'cod'` — admin may override a quote to Cash on Delivery;
 * gated at accept time by `companies.cod_enabled` (server-side via
 * `/errors/quote.cod_not_eligible`). The QuoteDetail wire DTO doesn't
 * currently expose `company.cod_enabled` so the option is always
 * selectable here; the server is the source of truth for eligibility.
 * Disclosed as a T11 deviation — adding a disabled-tooltip would
 * require a follow-up DTO extension on QuoteDetail.
 */
type PaymentTermsOverrideValue = "" | "immediate" | "net_30" | "net_60" | "cod";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_TONE: Record<QuoteStatus, Tone> = {
  requested: "warning",
  revised: "info",
  accepted: "success",
  rejected: "neutral",
  cancelled: "neutral",
  expired: "neutral",
};

const TERMINAL_STATUSES: ReadonlySet<QuoteStatus> = new Set<QuoteStatus>([
  "accepted",
  "rejected",
  "cancelled",
  "expired",
]);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Default `valid_until` for a fresh revision: now + 7 days, rounded to
 * the next full minute (matches the spec's "default `now() + 7d`").
 */
function defaultValidUntilIso(now: Date = new Date()): string {
  const d = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  d.setSeconds(0, 0);
  return d.toISOString();
}

/**
 * Render an ISO datetime as "yyyy-MM-ddTHH:mm" for an
 * `<input type="datetime-local">` value. Locale-naïve (no timezone
 * suffix) — the browser interprets the value in the local timezone and
 * the user's typed value is converted back via `new Date(value)` when
 * the form serialises.
 */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate(),
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ageLabel(requestedAtIso: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(requestedAtIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  if (days >= 1) return `${days}d`;
  const hours = Math.floor(ms / (1000 * 60 * 60));
  return `${hours}h`;
}

function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

/**
 * Translate audit-derived `state_history` rows into the AuditLogTimeline
 * shape. Stable ids derived from `at + to_status` since the api shape
 * doesn't surface a row id.
 */
function eventsFromHistory(history: QuoteDetail["state_history"]) {
  return history.map((tx, idx) => ({
    id: `${tx.at}-${tx.to_status}-${idx}`,
    at: tx.at,
    title: `${tx.from_status ?? "(initial)"} → ${tx.to_status}`,
    actor: `${tx.actor_kind}${
      tx.actor_id ? ` · ${tx.actor_id.slice(0, 8)}…` : ""
    }`,
    body: tx.metadata ? (
      <pre className="mt-1 overflow-x-auto rounded-sm bg-paper-warm p-2 text-caption">
        {JSON.stringify(tx.metadata, null, 2)}
      </pre>
    ) : undefined,
  }));
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const { notify, notifyProblem } = useToast();
  const [quote, setQuote] = useState<QuoteDetail | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Form state — initialised from loaded detail.
  const [validUntilLocal, setValidUntilLocal] = useState<string>("");
  const [paymentTermsOverride, setPaymentTermsOverride] =
    useState<PaymentTermsOverrideValue>("");
  const [adminNote, setAdminNote] = useState<string>("");
  const [editorRows, setEditorRows] = useState<QuoteEditorRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // ---- Fetch ----
  const refetch = useCallback(async () => {
    if (!id) return;
    try {
      const data = await api.get<QuoteDetail>(`/v1/admin/quotes/${id}`);
      setQuote(data);
      setLoadError(false);
      // Hydrate form state from loaded detail
      setValidUntilLocal(
        isoToLocalInput(data.valid_until ?? defaultValidUntilIso()),
      );
      setPaymentTermsOverride(data.payment_terms_override ?? "");
      setAdminNote(data.admin_note ?? "");
      setEditorRows(
        data.items.map((it) => ({
          variant_id: it.variant_id,
          quantity: String(it.quantity),
          quoted_unit_price_minor:
            it.quoted_unit_price_minor !== null
              ? String(it.quoted_unit_price_minor)
              : "",
          requested_unit_price_minor: it.requested_unit_price_minor,
          vat_category_code: it.vat_category_code,
          vat_rate_basis_points: it.vat_rate_basis_points,
          product_id: it.product_id,
          product_title: it.product_title,
          variant_title: it.variant_title,
          sku: it.sku,
        })),
      );
    } catch (err) {
      setLoadError(true);
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Failed to load quote");
    }
  }, [id, notify, notifyProblem]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // ---- Effective terms preview ----
  const effectiveTermsPreview: EffectivePaymentTerms | "—" = useMemo(() => {
    if (!quote) return "—";
    if (paymentTermsOverride !== "") return paymentTermsOverride;
    // When override is null, fall back to the service-collapsed default
    // (which IS the company payment terms when no override is set).
    return quote.effective_payment_terms;
  }, [quote, paymentTermsOverride]);

  // ---- Submit revision ----
  async function onSaveRevision() {
    if (!quote) return;
    // Convert the local-input string back to an ISO so the parser sees
    // the same shape the form-validation tests exercise.
    const validUntilIso = validUntilLocal
      ? new Date(validUntilLocal).toISOString()
      : "";
    const formState: ReviseFormState = {
      valid_until: validUntilIso,
      payment_terms_override: paymentTermsOverride,
      admin_note: adminNote,
      items: editorRows.map((r) => ({
        variant_id: r.variant_id,
        quantity: r.quantity,
        quoted_unit_price_minor: r.quoted_unit_price_minor,
      })),
    };
    const parsed = parseRevisionForm(formState);
    if (!parsed.ok || !parsed.body) {
      const firstError = Object.values(parsed.errors)[0] ?? "Invalid form";
      notify("error", `Validation failed: ${firstError}`);
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/v1/admin/quotes/${quote.id}/revise`, parsed.body);
      notify("success", "Quote revised");
      await refetch();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Revise failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Cancel quote ----
  async function onConfirmCancel() {
    if (!quote) return;
    setSubmitting(true);
    try {
      await api.post(`/v1/admin/quotes/${quote.id}/cancel`, {});
      notify("success", "Quote cancelled");
      setShowCancelModal(false);
      await refetch();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Cancel failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Render guards ----
  if (loadError && !quote) {
    return (
      <div className="flex flex-col gap-4">
        <Link to="/quotes" className="text-body-sm text-accent hover:underline">
          ← Back to quotes
        </Link>
        <p className="text-body-sm text-danger">Quote could not be loaded.</p>
      </div>
    );
  }

  if (!quote) {
    return <p className="text-body-sm text-ink-500">Loading…</p>;
  }

  const isTerminal = TERMINAL_STATUSES.has(quote.status);

  return (
    <div className="flex flex-col gap-6">
      <Link to="/quotes" className="text-body-sm text-accent hover:underline">
        ← Back to quotes
      </Link>

      {isTerminal ? (
        <Card className="p-4 bg-paper-warm">
          <p className="text-body-sm">
            This quote is <strong>{quote.status}</strong>
            {quote.source_order ? (
              <>
                {" "}— view order{" "}
                <Link
                  to={`/orders/${quote.source_order.id}`}
                  className="text-accent hover:underline"
                >
                  {shortId(quote.source_order.id)}
                </Link>
              </>
            ) : null}
            .
          </p>
        </Card>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[280px_1fr_320px]">
        {/* ------------------- Left: header + meta ------------------- */}
        <aside className="flex flex-col gap-4">
          <Card className="flex flex-col gap-3 p-5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
                  Quote
                </p>
                <h2 className="mt-1 font-serif text-title-md text-ink-900">
                  {quote.number}
                </h2>
              </div>
              <StatusPill tone={STATUS_TONE[quote.status]}>
                {quote.status}
              </StatusPill>
            </div>
            <dl className="flex flex-col gap-2 text-body-sm">
              <div className="flex justify-between gap-2">
                <dt className="text-ink-500">Requester</dt>
                <dd className="font-mono">
                  <Link
                    to={`/customers/${quote.requester_customer_id}`}
                    className="hover:underline"
                  >
                    {shortId(quote.requester_customer_id)}
                  </Link>
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ink-500">Company</dt>
                <dd className="font-mono">
                  <Link
                    to={`/companies/${quote.company_id}`}
                    className="hover:underline"
                  >
                    {shortId(quote.company_id)}
                  </Link>
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ink-500">Location</dt>
                <dd className="font-mono">
                  {shortId(quote.company_location_id)}
                </dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ink-500">Requested</dt>
                <dd>{formatTimestamp(quote.requested_at)}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-ink-500">Age</dt>
                <dd>{ageLabel(quote.requested_at)}</dd>
              </div>
            </dl>
          </Card>

          <Card className="flex flex-col gap-2 p-5">
            <p className="text-overline font-semibold uppercase tracking-[0.014em] text-ink-500">
              Effective terms preview
            </p>
            <p className="font-mono text-body-md text-ink-900">
              {effectiveTermsPreview}
            </p>
            {paymentTermsOverride === "" ? (
              <p className="text-caption text-ink-500">
                Inheriting from company default.
              </p>
            ) : (
              <p className="text-caption text-ink-500">
                Overriding the company default.
              </p>
            )}
          </Card>
        </aside>

        {/* ------------------- Center: items editor ------------------- */}
        <section className="flex min-w-0 flex-col gap-4">
          <h3 className="font-serif text-title-md text-ink-900">Items</h3>
          <QuoteItemsEditor
            rows={editorRows}
            onChange={setEditorRows}
            disabled={isTerminal}
          />
        </section>

        {/* ------------------- Right: meta + actions ------------------- */}
        <aside className="flex flex-col gap-4 lg:sticky lg:top-24 lg:self-start">
          <Card className="flex flex-col gap-4 p-5">
            <h3 className="font-serif text-title-md text-ink-900">Revision</h3>
            <div className="flex flex-col gap-1">
              <label
                htmlFor="valid-until"
                className="text-body-sm font-medium text-ink-700"
              >
                Valid until
              </label>
              <input
                id="valid-until"
                type="datetime-local"
                value={validUntilLocal}
                onChange={(e) => setValidUntilLocal(e.target.value)}
                disabled={isTerminal}
                className="rounded-sm border border-ink-300 px-2 py-1 text-body-sm"
              />
            </div>
            <Select
              label="Payment terms override"
              value={paymentTermsOverride}
              onChange={(e) =>
                setPaymentTermsOverride(
                  e.target.value as PaymentTermsOverrideValue,
                )
              }
              disabled={isTerminal}
            >
              <option value="">(inherit)</option>
              <option value="immediate">immediate</option>
              <option value="net_30">net_30</option>
              <option value="net_60">net_60</option>
              <option value="cod">cod</option>
            </Select>
            <Textarea
              label="Admin note"
              rows={3}
              maxLength={2000}
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
              disabled={isTerminal}
              helper={`${adminNote.length}/2000`}
            />

            <Hairline />

            <dl className="flex flex-col gap-1 text-body-sm">
              <div className="flex justify-between">
                <dt className="text-ink-500">Net</dt>
                <dd>
                  <Numeric>{formatGBP(quote.totals.net_minor)}</Numeric>
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-ink-500">VAT</dt>
                <dd>
                  <Numeric>{formatGBP(quote.totals.vat_minor)}</Numeric>
                </dd>
              </div>
              <div className="flex justify-between font-medium text-ink-900">
                <dt>Gross (loaded)</dt>
                <dd>
                  <Numeric serif>
                    {formatGBP(quote.totals.gross_minor)}
                  </Numeric>
                </dd>
              </div>
            </dl>
            <p className="text-caption text-ink-500">
              Loaded totals reflect the persisted quote. Live totals in
              the items editor update as you type.
            </p>

            <Hairline />

            {!isTerminal ? (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => void onSaveRevision()}
                  disabled={submitting}
                  loading={submitting}
                >
                  Save revision
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowCancelModal(true)}
                  disabled={submitting}
                >
                  Cancel quote
                </Button>
              </div>
            ) : (
              <p className="text-body-sm text-ink-500">
                No actions available in this state.
              </p>
            )}
          </Card>

          <Card className="flex flex-col gap-3 p-5">
            <h3 className="font-serif text-title-md text-ink-900">
              Audit trail
            </h3>
            {quote.state_history.length === 0 ? (
              <p className="text-body-sm text-ink-500">No transitions yet.</p>
            ) : (
              <AuditLogTimeline events={eventsFromHistory(quote.state_history)} />
            )}
          </Card>
        </aside>
      </div>

      {showCancelModal ? (
        <Modal
          open
          onClose={() => setShowCancelModal(false)}
          title="Cancel quote"
          size="sm"
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => setShowCancelModal(false)}
                disabled={submitting}
              >
                Back
              </Button>
              <Button
                variant="danger"
                onClick={() => void onConfirmCancel()}
                disabled={submitting}
                loading={submitting}
              >
                Cancel quote
              </Button>
            </>
          }
        >
          <p className="text-body-sm">
            Cancelling moves quote <strong>{quote.number}</strong> to a
            terminal state. This action cannot be undone.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
